// The card a run ends on (#1854, not gated). The core finished: the closing
// card — a few more keys and the live hub. The core left with «Выйти»: a note
// on where the tutorial lives. A chapter, either way: what happened, and the
// live hub to go on from. All of them offer to delete the tutorial project.
//
// A finished unit whose steps had the user set prefs the setup wrote, left
// different from before, asks «Оставить эти настройки?» first (ticket 11):
// «Вернуть как было» puts them back, «Оставить» — or no answer — keeps them.
//
// Non-modal and never focused, like the step card: the caret stays in the
// scene ⌘Enter just made. It sits above the bottom-right corner, which the
// support widget takes.

import { useTranslation } from 'react-i18next';
import type { RunOutcome, TutorialUnit } from '@/types';
import { formatKeybinding } from '@/utils/fountain/platformKeys';
import type { BooleanPrefKey, PrefToKeep } from './prefLedger';
import { TutorialHubList } from './TutorialHubList';
import { overlayAttr, TourButton } from './TutorialOverlay';

/** The closing card's keys: [binding, copy id]. */
const MORE_KEYS = [
    ['Shift+Enter', 'soft-line'],
    ['Ctrl+D', 'draft'],
    ['Ctrl+Z / Ctrl+Shift+Z', 'undo'],
] as const;

const PREFIXES = [
    ['.', 'scene'],
    ['@', 'character'],
    ['!', 'action'],
    ['>', 'transition'],
] as const;

/** The names the question lists the prefs by: the visibility popover's, and
 *  the navigator toggle's. */
const PREF_LABELS: Partial<Record<BooleanPrefKey, string>> = {
    show_navigator: 'tutorial.end.keep.navigator',
    issue_checks: 'issues.checks.title',
    hide_drafts: 'fountain.hierarchy.drafts',
};

interface TutorialEndCardProps {
    unit: TutorialUnit;
    outcome: RunOutcome;
    /** The prefs to ask about; nothing to ask when absent. */
    keepPrefs?: readonly PrefToKeep[];
    /** «Оставить» (`true`) or «Вернуть как было» (`false`). */
    onKeepPrefs?: (keep: boolean) => void;
    onClose: () => void;
    /** «Удалить учебный проект». */
    onDelete: () => void;
}

export function TutorialEndCard({ unit, outcome, keepPrefs, onKeepPrefs, onClose, onDelete }: TutorialEndCardProps) {
    const { t } = useTranslation();

    return (
        <div
            {...overlayAttr}
            data-testid="tutorial-end-card"
            data-unit={unit}
            data-outcome={outcome}
            className="fixed right-6 bottom-24 z-[3500] flex w-[340px] flex-col gap-3 rounded-2xl bg-c24 p-4 text-sm text-text-secondary shadow-lg"
        >
            {unit !== 'core' ? (
                <>
                    <div className="font-semibold">
                        {t(outcome === 'done' ? 'tutorial.end.chapter-done-title' : 'tutorial.end.chapter-skipped-title', {
                            title: t(`tutorial.units.${unit}.title`),
                        })}
                    </div>
                    {keepPrefs && keepPrefs.length > 0 && (
                        <div data-testid="tutorial-keep-prefs" className="flex flex-col gap-2 border-t border-white/10 pt-3">
                            <div className="leading-snug">
                                {t('tutorial.end.keep.question')} <b>{keepPrefs.map(({ key }) => t(PREF_LABELS[key] ?? key)).join(', ')}</b>
                            </div>
                            <div className="-mx-2 flex justify-end">
                                <TourButton onClick={() => onKeepPrefs?.(false)}>{t('tutorial.end.keep.revert')}</TourButton>
                                <TourButton primary onClick={() => onKeepPrefs?.(true)}>
                                    {t('tutorial.end.keep.keep')}
                                </TourButton>
                            </div>
                        </div>
                    )}
                    <Hub label={t('tutorial.end.hub')} />
                </>
            ) : outcome === 'done' ? (
                <>
                    <div className="font-semibold">{t('tutorial.end.done-title')}</div>
                    <ul className="flex flex-col gap-1 leading-snug">
                        {MORE_KEYS.map(([binding, id]) => (
                            <li key={id}>
                                <b>{formatKeybinding(binding)}</b> — {t(`tutorial.end.keys.${id}`)}
                            </li>
                        ))}
                        <li>
                            {t('tutorial.end.prefixes')}{' '}
                            {PREFIXES.map(([prefix, id], index) => (
                                <span key={id}>
                                    {index > 0 && ', '}
                                    <b>{prefix}</b> {t(`tutorial.end.prefix.${id}`)}
                                </span>
                            ))}
                        </li>
                    </ul>
                    <div>
                        {t('tutorial.end.all-keys')} <b>{formatKeybinding('Ctrl+/')}</b>
                    </div>
                    <Hub label={t('tutorial.end.chapters')} />
                </>
            ) : (
                <>
                    <div className="font-semibold">{t('tutorial.end.skipped-title')}</div>
                    <div className="leading-snug">{t('tutorial.end.skipped-body')}</div>
                </>
            )}
            <div className="-mx-2 -mb-2 flex justify-between">
                <TourButton onClick={onDelete}>{t('tutorial.end.delete-project')}</TourButton>
                <TourButton primary onClick={onClose}>
                    {t('tutorial.end.close')}
                </TourButton>
            </div>
        </div>
    );
}

function Hub({ label }: { label: string }) {
    return (
        <div className="flex flex-col gap-1 border-t border-white/10 pt-3">
            <div className="text-xs opacity-60">{label}</div>
            <TutorialHubList surface="card" />
        </div>
    );
}
